import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { from, Observable, of } from 'rxjs';

/**
 * Preloads only routes marked `data: { preload: true }`, politely.
 *
 * The accounting menu's pages are marked (accounting-routing.module.ts). Their parent
 * route is not, so nothing extra is downloaded until someone opens Accounting.
 *
 * The router asks for every marked route at once. Fetched together they compete with
 * the page that was just opened: on the first visit that made Accounting lag while
 * dozens of chunks were downloaded and parsed. So they are fetched one at a time, each
 * only when the browser is idle, starting a few seconds after the first page is up.
 */
@Injectable({ providedIn: 'root' })
export class AccountingPreloadingStrategy implements PreloadingStrategy {
  private queue: Promise<unknown> = new Promise((resolve) => setTimeout(resolve, 4000));

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload']) return of(null);
    const next = this.queue.then(() => whenIdle()).then(() => new Promise((resolve) => {
      // A chunk that fails to load is left for the router to fetch on demand.
      load().subscribe({ complete: () => resolve(null), error: () => resolve(null) });
    }));
    this.queue = next;
    return from(next);
  }
}

/** Resolves when the browser has a quiet moment (or after 2s if it never says so). */
function whenIdle(): Promise<void> {
  return new Promise((resolve) => {
    const idle = (window as any).requestIdleCallback as ((cb: () => void, opts?: { timeout: number }) => number) | undefined;
    if (idle) idle(() => resolve(), { timeout: 2000 });
    else setTimeout(resolve, 200);
  });
}
